import { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowPathIcon,
  XMarkIcon,
  StarIcon,
  CalendarIcon,
  NoSymbolIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import { useTranslation } from '../../i18n';
import { Hotel } from '../../types/hotel';
import {
  fetchHotels,
  createHotel,
  updateHotel,
  deleteHotel,
  suspendHotel,
  activateHotel,
  fetchHotelDetailStats,
  bulkSuspendHotels,
  bulkActivateHotels,
  bulkDeleteHotels,
  normalizeSector,
  SECTOR_META,
  HotelSearchParams,
  HotelDetailStats,
  SectorKey,
} from '../../services/hotelAdminService';

const SECTOR_TABS: { key: SectorKey | 'ALL'; label: string }[] = [
  { key: 'HOTEL', label: 'Hôtels' },
  { key: 'RESTAURANT', label: 'Restaurants & Cafés' },
  { key: 'OTHER', label: 'Lounges & Bars' },
  { key: 'TOURISM', label: 'Attractions' },
  { key: 'ALL', label: 'Tous' },
];

/** LED indicator for FPT payment status */
function PaymentStatusLED({ status }: { status?: string }) {
  const config: Record<string, { color: string; label: string }> = {
    paid:    { color: 'bg-green-500', label: 'À jour' },
    partial: { color: 'bg-orange-400', label: 'Partiel' },
    overdue: { color: 'bg-red-500', label: 'Impayé' },
    none:    { color: 'bg-gray-300', label: 'Aucune taxe' },
    unknown: { color: 'bg-gray-300', label: 'N/A' },
  };
  const { color, label } = config[status || 'unknown'] || config.unknown;
  return (
    <span className="inline-flex items-center gap-1.5" title={`FPT: ${label}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${color} inline-block`} aria-hidden="true" />
      <span className="text-xs text-gray-600">{label}</span>
    </span>
  );
}

function computeFPTStatus(hotel: Hotel): string {
  if (hotel.fpt_status === 'none' || hotel.fpt_status === 'paid' || hotel.fpt_status === 'partial' || hotel.fpt_status === 'overdue' || hotel.fpt_status === 'unknown') {
    return hotel.fpt_status;
  }
  const due = hotel.fpt_total_due;
  const paid = hotel.fpt_total_paid ?? 0;
  if (due === undefined || due === null) return 'unknown';
  if (due <= 0) return paid > 0 ? 'paid' : 'none';
  if (paid >= due) return 'paid';
  if (paid > 0) return 'partial';
  return 'overdue';
}

interface HotelFormData {
  name: string;
  address: string;
  province: string;
  phone: string;
  email: string;
  total_rooms: number;
  category: number;
  owner_name: string;
  sector: SectorKey;
}

const emptyForm: HotelFormData = {
  name: '',
  address: '',
  province: '',
  phone: '',
  email: '',
  total_rooms: 1,
  category: 1,
  owner_name: '',
  sector: 'HOTEL',
};

export default function HotelManagementPage() {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sectorTab, setSectorTab] = useState<SectorKey | 'ALL'>('HOTEL');
  const [dateAfter, setDateAfter] = useState('');
  const [dateBefore, setDateBefore] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState<'suspend' | 'activate' | 'delete' | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [detailStats, setDetailStats] = useState<HotelDetailStats | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [formData, setFormData] = useState<HotelFormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const loadHotels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: HotelSearchParams = { limit: 100 };
      if (searchTerm) params.search = searchTerm;
      if (provinceFilter) params.province = provinceFilter;
      if (categoryFilter) params.category = categoryFilter as number;
      if (statusFilter) params.status = statusFilter as 'active' | 'suspended' | 'pending';
      if (sectorTab !== 'ALL') params.sector = sectorTab;
      if (dateAfter) params.registered_after = dateAfter;
      if (dateBefore) params.registered_before = dateBefore;
      const result = await fetchHotels(params);
      let list = Array.isArray(result.data) ? result.data : [];
      // Client-side sector guard
      if (sectorTab !== 'ALL') {
        list = list.filter((h) => normalizeSector(h.sector) === sectorTab);
      }
      setHotels(list);
      setTotal(result.total ?? list.length);
    } catch (err: any) {
      setError(err.message || t('hotels.errorLoading'));
      setHotels([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, provinceFilter, categoryFilter, statusFilter, sectorTab, dateAfter, dateBefore, t]);

  useEffect(() => {
    loadHotels();
  }, [loadHotels]);

  // Clear success message after 3s
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Derived data
  const provinces = Array.from(new Set(hotels.map(h => h.province).filter(Boolean))).sort();
  const activeCount = hotels.filter(h => h.is_active).length;
  const suspendedCount = hotels.filter(h => !h.is_active && h.status !== 'pending').length;
  const totalRooms = hotels.reduce((sum, h) => sum + (h.total_rooms ?? h.totalRooms ?? 0), 0);

  // CRUD handlers
  const handleCreateHotel = async () => {
    setSaving(true);
    setActionError(null);
    try {
      await createHotel({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        province: formData.province,
        total_rooms: formData.total_rooms,
        category: formData.category,
        owner_name: formData.owner_name,
        sector: formData.sector || (sectorTab === 'ALL' ? 'HOTEL' : sectorTab),
      });
      setShowCreateModal(false);
      setFormData({ ...emptyForm });
      setSuccessMsg(t('hotelMgmt.createSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditHotel = async () => {
    if (!selectedHotel) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateHotel(selectedHotel.id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        province: formData.province,
        total_rooms: formData.total_rooms,
        category: formData.category,
        owner_name: formData.owner_name,
      });
      setShowEditModal(false);
      setSelectedHotel(null);
      setFormData({ ...emptyForm });
      setSuccessMsg(t('hotelMgmt.editSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHotel = async (hotel: Hotel) => {
    if (!window.confirm(t('hotelMgmt.confirmDelete').replace('{name}', hotel.name))) return;
    setActionError(null);
    try {
      await deleteHotel(hotel.id);
      setSuccessMsg(t('hotelMgmt.deleteSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    }
  };

  const handleSuspend = async () => {
    if (!selectedHotel || !suspendReason.trim()) return;
    setActionError(null);
    try {
      await suspendHotel(selectedHotel.id, suspendReason.trim());
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedHotel(null);
      setSuccessMsg(t('hotelMgmt.suspendSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    }
  };

  const handleActivate = async (hotel: Hotel) => {
    if (!window.confirm(t('hotelMgmt.confirmActivate').replace('{name}', hotel.name))) return;
    setActionError(null);
    try {
      await activateHotel(hotel.id);
      setSuccessMsg(t('hotelMgmt.activateSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    }
  };

  const handleViewDetail = async (hotel: Hotel) => {
    setSelectedHotel(hotel);
    setShowDetailPanel(true);
    setDetailLoading(true);
    setDetailStats(null);
    try {
      const stats = await fetchHotelDetailStats(hotel.id);
      setDetailStats(stats);
    } catch {
      setDetailStats(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openEditModal = (hotel: Hotel) => {
    setSelectedHotel(hotel);
    setFormData({
      name: hotel.name,
      address: hotel.address,
      province: hotel.province,
      phone: hotel.phone,
      email: hotel.email,
      total_rooms: hotel.total_rooms ?? hotel.totalRooms ?? 0,
      category: hotel.category ?? 1,
      owner_name: hotel.owner_name ?? hotel.ownerName ?? '',
      sector: normalizeSector(hotel.sector),
    });
    setActionError(null);
    setShowEditModal(true);
  };

  const openSuspendModal = (hotel: Hotel) => {
    setSelectedHotel(hotel);
    setSuspendReason('');
    setActionError(null);
    setShowSuspendModal(true);
  };

  // Bulk operations
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === hotels.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(hotels.map(h => h.id)));
    }
  };

  const handleBulkAction = async () => {
    if (!showBulkModal || selectedIds.size === 0) return;
    setBulkLoading(true);
    setActionError(null);
    try {
      const ids = Array.from(selectedIds);
      if (showBulkModal === 'suspend') {
        await bulkSuspendHotels(ids, suspendReason || 'Bulk suspension');
      } else if (showBulkModal === 'activate') {
        await bulkActivateHotels(ids);
      } else if (showBulkModal === 'delete') {
        await bulkDeleteHotels(ids);
      }
      setShowBulkModal(null);
      setSuspendReason('');
      setSelectedIds(new Set());
      setSuccessMsg(t('hotelMgmt.bulkSuccess'));
      await loadHotels();
    } catch (err: any) {
      setActionError(err.message || t('common.error'));
    } finally {
      setBulkLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setProvinceFilter('');
    setCategoryFilter('');
    setStatusFilter('');
    setDateAfter('');
    setDateBefore('');
  };

  const hasActiveFilters = searchTerm || provinceFilter || categoryFilter || statusFilter || dateAfter || dateBefore;
  const capacityHeader =
    sectorTab === 'ALL' ? 'Capacité' : SECTOR_META[sectorTab].capacity;

  const getStatusBadge = (hotel: Hotel) => {
    const status = hotel.status;
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
          {t('hotelMgmt.statusPending')}
        </span>
      );
    }
    if (hotel.is_active) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          {t('hotelMgmt.statusActive')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircleIcon className="w-3 h-3 mr-1" />
        {t('hotelMgmt.statusSuspended')}
      </span>
    );
  };

  const renderStars = (count: number) => (
    <span className="inline-flex" aria-label={`${count} ${t('hotelMgmt.stars')}`}>
      {Array.from({ length: count }, (_, i) => (
        <StarIcon key={i} className="w-3.5 h-3.5 text-yellow-500 fill-current" />
      ))}
    </span>
  );

  // Form modal
  const renderFormModal = (title: string, onSubmit: () => void, onClose: () => void) => (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose}></div>
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          {actionError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{actionError}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldName')} *</label>
              <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldOwner')} *</label>
              <input type="text" value={formData.owner_name} onChange={e => setFormData({ ...formData, owner_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldEmail')} *</label>
                <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldPhone')} *</label>
                <input type="text" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldAddress')} *</label>
              <input type="text" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldProvince')} *</label>
              <input type="text" value={formData.province} onChange={e => setFormData({ ...formData, province: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {SECTOR_META[formData.sector || 'HOTEL'].capacity} *
                </label>
                <input type="number" min="1" value={formData.total_rooms} onChange={e => setFormData({ ...formData, total_rooms: parseInt(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('hotelMgmt.fieldCategory')}</label>
                <select value={formData.category} onChange={e => setFormData({ ...formData, category: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {t('hotelMgmt.stars')}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Secteur *</label>
              <select
                value={formData.sector || 'HOTEL'}
                onChange={e => setFormData({ ...formData, sector: e.target.value as SectorKey })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {(Object.keys(SECTOR_META) as SectorKey[]).map((s) => (
                  <option key={s} value={s}>{SECTOR_META[s].label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button onClick={onSubmit} disabled={saving || !formData.name || !formData.email || !formData.owner_name}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {saving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Loading skeleton
  if (loading && hotels.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-4 gap-6 mb-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>)}
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-gray-200 rounded mb-3"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Établissements</h1>
              <p className="text-gray-600 mt-1">Hôtels, restaurants, lounges et attractions — supervision CNS</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={loadHotels} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" title={t('dashboard.reload')}>
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {hasPermission('HOTELS_CREATE') && (
              <button onClick={() => {
                setFormData({ ...emptyForm, sector: sectorTab === 'ALL' ? 'HOTEL' : sectorTab });
                setActionError(null);
                setShowCreateModal(true);
              }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                <PlusIcon className="w-4 h-4 mr-2" />
                Nouvel établissement
              </button>
            )}
          </div>
        </div>

        {/* Sector tabs */}
        <div className="mt-4 bg-white rounded-lg shadow p-1.5 flex flex-wrap gap-1" role="tablist">
          {SECTOR_TABS.map((tab) => {
            const active = sectorTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSectorTab(tab.key)}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                  active ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Success / Error banners */}
      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
          <p className="text-sm text-green-700">{successMsg}</p>
        </div>
      )}
      {(error || actionError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error || actionError}</p>
          <button onClick={() => { setError(null); setActionError(null); }} className="text-red-400 hover:text-red-600">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('hotelMgmt.totalHotels')}</p>
              <p className="text-2xl font-bold text-gray-900">{total || hotels.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('hotelMgmt.activeHotels')}</p>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <XCircleIcon className="h-8 w-8 text-red-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('hotelMgmt.suspendedHotels')}</p>
              <p className="text-2xl font-bold text-gray-900">{suspendedCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-purple-600 flex-shrink-0" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('hotelMgmt.totalRooms')}</p>
              <p className="text-2xl font-bold text-gray-900">{totalRooms}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="relative md:col-span-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder={t('hotelMgmt.searchPlaceholder')} value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <select value={provinceFilter} onChange={e => setProvinceFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{t('hotelMgmt.allProvinces')}</option>
              {provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value ? parseInt(e.target.value) : '')}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{t('hotelMgmt.allCategories')}</option>
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {t('hotelMgmt.stars')}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{t('hotelMgmt.allStatuses')}</option>
              <option value="active">{t('hotelMgmt.statusActive')}</option>
              <option value="suspended">{t('hotelMgmt.statusSuspended')}</option>
              <option value="pending">{t('hotelMgmt.statusPending')}</option>
            </select>
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600">{hotels.length} {t('hotelMgmt.results')}</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-800 underline">
                  {t('hotelMgmt.clearFilters')}
                </button>
              )}
            </div>
          </div>
          {/* Date range filters */}
          <div className="mt-4 flex items-center space-x-4">
            <CalendarIcon className="w-4 h-4 text-gray-400" />
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">{t('hotelMgmt.registeredAfter')}</label>
              <input type="date" value={dateAfter} onChange={e => setDateAfter(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">{t('hotelMgmt.registeredBefore')}</label>
              <input type="date" value={dateBefore} onChange={e => setDateBefore(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <span className="text-sm text-blue-800 font-medium">
            {selectedIds.size} {t('hotelMgmt.selected')}
          </span>
          <div className="flex items-center space-x-3">
            {hasPermission('HOTELS_SUSPEND') && (
              <button onClick={() => { setSuspendReason(''); setShowBulkModal('suspend'); }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-md hover:bg-orange-200">
                <NoSymbolIcon className="w-4 h-4 mr-1" />
                {t('hotelMgmt.bulkSuspend')}
              </button>
            )}
            {hasPermission('HOTELS_SUSPEND') && (
              <button onClick={() => setShowBulkModal('activate')}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200">
                <CheckIcon className="w-4 h-4 mr-1" />
                {t('hotelMgmt.bulkActivate')}
              </button>
            )}
            {hasPermission('HOTELS_DELETE') && (
              <button onClick={() => setShowBulkModal('delete')}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200">
                <TrashIcon className="w-4 h-4 mr-1" />
                {t('hotelMgmt.bulkDelete')}
              </button>
            )}
            <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-600 hover:text-gray-800 underline">
              {t('hotelMgmt.clearSelection')}
            </button>
          </div>
        </div>
      )}

      {/* Hotels table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input type="checkbox" checked={selectedIds.size === hotels.length && hotels.length > 0}
                    onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Établissement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Secteur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hotelMgmt.colLocation')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hotelMgmt.colCategory')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{capacityHeader}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hotelMgmt.colStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FPT</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('hotelMgmt.colActions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {hotels.map(hotel => {
                const rooms = hotel.total_rooms ?? hotel.totalRooms ?? 0;
                const occupied = hotel.current_occupancy ?? hotel.occupiedRooms ?? 0;
                const occRate = rooms > 0 ? Math.round((occupied / rooms) * 100) : 0;
                return (
                  <tr key={hotel.id} className={`hover:bg-gray-50 ${selectedIds.has(hotel.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={selectedIds.has(hotel.id)} onChange={() => toggleSelect(hotel.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{hotel.name}</div>
                      <div className="text-xs text-gray-500 flex items-center mt-0.5">
                        <EnvelopeIcon className="w-3 h-3 mr-1" />{hotel.email}
                      </div>
                      {(hotel.owner_name || hotel.ownerName) && (
                        <div className="text-xs text-gray-400 mt-0.5">{t('hotelMgmt.owner')}: {hotel.owner_name || hotel.ownerName}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-800">
                        {SECTOR_META[normalizeSector(hotel.sector)].short}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <MapPinIcon className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0" />
                        <div>
                          <div>{hotel.address}</div>
                          <div className="text-gray-500">{hotel.province}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hotel.category ? renderStars(hotel.category) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900">{rooms}</div>
                      <div className="text-gray-500">{occRate}% {t('hotelMgmt.occupied')}</div>
                      <div className="w-16 bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${Math.min(occRate, 100)}%` }}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(hotel)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <PaymentStatusLED status={computeFPTStatus(hotel)} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handleViewDetail(hotel)} className="text-blue-600 hover:text-blue-900" title={t('hotelMgmt.viewDetails')}>
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {hasPermission('HOTELS_EDIT') && (
                          <button onClick={() => openEditModal(hotel)} className="text-indigo-600 hover:text-indigo-900" title={t('hotels.edit')}>
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {hasPermission('HOTELS_SUSPEND') && hotel.is_active && (
                          <button onClick={() => openSuspendModal(hotel)} className="text-orange-600 hover:text-orange-900" title={t('hotels.suspend')}>
                            <NoSymbolIcon className="w-4 h-4" />
                          </button>
                        )}
                        {hasPermission('HOTELS_SUSPEND') && !hotel.is_active && hotel.status !== 'pending' && (
                          <button onClick={() => handleActivate(hotel)} className="text-green-600 hover:text-green-900" title={t('hotels.activate')}>
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                        {hasPermission('HOTELS_DELETE') && (
                          <button onClick={() => handleDeleteHotel(hotel)} className="text-red-600 hover:text-red-900" title={t('hotels.delete')}>
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {hotels.length === 0 && !loading && (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('hotels.noData')}</p>
            {error && (
              <button onClick={loadHotels} className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                <ArrowPathIcon className="w-4 h-4 mr-2" />{t('common.retry')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Hotel Detail Slide-over Panel */}
      {showDetailPanel && selectedHotel && (
        <div className="fixed inset-0 z-50 overflow-hidden" aria-modal="true" role="dialog">
          <div className="absolute inset-0 bg-gray-500 bg-opacity-50" onClick={() => { setShowDetailPanel(false); setSelectedHotel(null); }}></div>
          <div className="absolute inset-y-0 right-0 max-w-lg w-full bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{selectedHotel.name}</h2>
                <button onClick={() => { setShowDetailPanel(false); setSelectedHotel(null); }} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Hotel info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center text-sm text-gray-600">
                  <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {selectedHotel.address}, {selectedHotel.province}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {selectedHotel.email}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <PhoneIcon className="w-4 h-4 mr-2 text-gray-400" />
                  {selectedHotel.phone}
                </div>
                {(selectedHotel.owner_name || selectedHotel.ownerName) && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{t('hotelMgmt.owner')}:</span> {selectedHotel.owner_name || selectedHotel.ownerName}
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  {selectedHotel.category && renderStars(selectedHotel.category)}
                  {getStatusBadge(selectedHotel)}
                </div>
                <div className="text-xs text-gray-400">
                  {t('hotelMgmt.registeredOn')} {new Date(selectedHotel.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Statistics */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2 text-blue-600" />
                {t('hotelMgmt.statistics')}
              </h3>
              {detailLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded"></div>)}
                </div>
              ) : detailStats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-medium">{t('hotelMgmt.statTotalGuests')}</p>
                    <p className="text-xl font-bold text-blue-900">{detailStats.total_guests}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-xs text-green-600 font-medium">{t('hotelMgmt.statOccupancy')}</p>
                    <p className="text-xl font-bold text-green-900">{detailStats.occupancy_rate}%</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-xs text-purple-600 font-medium">{t('hotelMgmt.statRooms')}</p>
                    <p className="text-xl font-bold text-purple-900">{detailStats.current_occupancy}/{detailStats.total_rooms}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-xs text-orange-600 font-medium">{t('hotelMgmt.statAvgStay')}</p>
                    <p className="text-xl font-bold text-orange-900">{detailStats.average_stay_duration} {t('hotelMgmt.days')}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600 font-medium">{t('hotelMgmt.statCheckinsToday')}</p>
                    <p className="text-xl font-bold text-gray-900">{detailStats.checkins_today}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-600 font-medium">{t('hotelMgmt.statCheckinsMonth')}</p>
                    <p className="text-xl font-bold text-gray-900">{detailStats.checkins_this_month}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t('hotelMgmt.noStats')}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {showSuspendModal && selectedHotel && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowSuspendModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('hotelMgmt.suspendTitle')} — {selectedHotel.name}
              </h3>
              {actionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{actionError}</div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('hotelMgmt.suspendReason')} *</label>
                <textarea rows={3} value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-orange-500 focus:border-orange-500"
                  placeholder={t('hotelMgmt.suspendPlaceholder')} />
              </div>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowSuspendModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                  {t('common.cancel')}
                </button>
                <button onClick={handleSuspend} disabled={!suspendReason.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm hover:bg-orange-700 disabled:opacity-50">
                  {t('hotels.suspend')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowBulkModal(null)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {showBulkModal === 'suspend' && t('hotelMgmt.bulkSuspendTitle')}
                {showBulkModal === 'activate' && t('hotelMgmt.bulkActivateTitle')}
                {showBulkModal === 'delete' && t('hotelMgmt.bulkDeleteTitle')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('hotelMgmt.bulkConfirm').replace('{count}', String(selectedIds.size))}
              </p>
              {showBulkModal === 'suspend' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('hotelMgmt.suspendReason')}</label>
                  <textarea rows={2} value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" />
                </div>
              )}
              {actionError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{actionError}</div>
              )}
              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowBulkModal(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
                  {t('common.cancel')}
                </button>
                <button onClick={handleBulkAction} disabled={bulkLoading}
                  className={`px-4 py-2 rounded-md text-sm text-white disabled:opacity-50 ${
                    showBulkModal === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                    showBulkModal === 'suspend' ? 'bg-orange-600 hover:bg-orange-700' :
                    'bg-green-600 hover:bg-green-700'
                  }`}>
                  {bulkLoading ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && renderFormModal(
        t('hotelMgmt.newHotel'),
        handleCreateHotel,
        () => { setShowCreateModal(false); setActionError(null); }
      )}

      {/* Edit modal */}
      {showEditModal && renderFormModal(
        `${t('hotels.edit')}: ${selectedHotel?.name}`,
        handleEditHotel,
        () => { setShowEditModal(false); setSelectedHotel(null); setActionError(null); }
      )}
    </div>
  );
}
